import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { faPauseCircle, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

let currentlyPlaying: WaveSurfer | null = null;

const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const ProfessionalPageMessageAudioPlayer = ({ audioUrl, duration }) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const waveSurferRef = useRef<WaveSurfer | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);

    useEffect(() => {
        if (!containerRef.current) return;
        if (waveSurferRef.current) {
            waveSurferRef.current.destroy();
        }

        const wavesurfer = WaveSurfer.create({
            container: containerRef.current,
            waveColor: "#2AF598",
            progressColor: "#009EFD",
            barWidth: 4,
            barGap: 2,
            barRadius: 5,
            barHeight: 3,
            height: 50,
        });

        waveSurferRef.current = wavesurfer;
        wavesurfer.load(audioUrl);

        wavesurfer.on("audioprocess", () => {
            setCurrentTime(wavesurfer.getCurrentTime());
        });

        wavesurfer.on("interaction", () => {
            setCurrentTime(wavesurfer.getCurrentTime());
        });

        wavesurfer.on("finish", () => {
            setIsPlaying(false);
            setCurrentTime(0);
        });

        return () => {
            wavesurfer.destroy();
        };
    }, [audioUrl]);

    const togglePlayPause = () => {
        const player = waveSurferRef.current;
        if (!player) return;

        if (!isPlaying) {
            if (currentlyPlaying && currentlyPlaying !== player) {
                currentlyPlaying.pause();
                currentlyPlaying = null;
            }
            window.dispatchEvent(new Event("pauseAllPlayers"));
            player.play();
            currentlyPlaying = player;
            setIsPlaying(true);
        } else {
            player.pause();
            if (currentlyPlaying === player) {
                currentlyPlaying = null;
            }
            setIsPlaying(false);
        }
    };

    useEffect(() => {
        const handlePauseAll = () => {
            const player = waveSurferRef.current;
            if (player && player !== currentlyPlaying) {
                player.pause();
                setIsPlaying(false);
            }
        };
        window.addEventListener("pauseAllPlayers", handlePauseAll);
        return () => {
            window.removeEventListener("pauseAllPlayers", handlePauseAll);
        };
    }, []);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <FontAwesomeIcon
                    icon={isPlaying ? faPauseCircle : faPlayCircle}
                    aria-hidden="true"
                    style={{ fontSize: "1.8rem", cursor: "pointer" }}
                    onClick={togglePlayPause}
                />
                <div ref={containerRef} className="p-2"
                    style={{ flex: 1, borderRadius: "10px", background: "white" }}></div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", fontSize: "0.875rem", color: "white", fontWeight: "bold" }}>
                {formatTime(currentTime)} / {duration}
            </div>
        </div>
    );
};

export default ProfessionalPageMessageAudioPlayer;